import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  CircularProgress,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";
import ButtonOne from "../../common/normal-button";
import { useAuth } from "../../hooks/AuthProvider";
import { VOICE_CONFIG_ENDPOINT } from "../../config/urls";

// The "WhatsApp Voice" page under the sidebar's Configuration section
// (add-whatsapp-voice-settings). For now: one toggle (voice_enabled) and
// a Save button — built so more settings can be added to this same page
// later (design.md Decision 3). Saving isn't just a local write: it
// calls Gupshup's Partner API server-side and only actually takes effect
// if Gupshup confirms it (design.md Decision 2) — a failed save reverts
// the toggle to the last-saved value instead of leaving it showing an
// unconfirmed change.
const WhatsAppVoiceSettings = () => {
  const { getValidToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [savedVoiceEnabled, setSavedVoiceEnabled] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const authHeaders = async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    return {
      Authorization: `Bearer ${token}`,
      "X-Tenant-ID": tenant,
    };
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        const response = await axios.get(VOICE_CONFIG_ENDPOINT, { headers });
        if (cancelled) return;
        const enabled = Boolean(response.data?.voice_enabled);
        setVoiceEnabled(enabled);
        setSavedVoiceEnabled(enabled);
      } catch {
        if (!cancelled) {
          setSnackbar({ open: true, message: "Could not load WhatsApp Voice settings.", severity: "error" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = await authHeaders();
      const response = await axios.patch(
        VOICE_CONFIG_ENDPOINT,
        { voice_enabled: voiceEnabled },
        { headers },
      );
      const enabled = Boolean(response.data?.voice_enabled);
      setVoiceEnabled(enabled);
      setSavedVoiceEnabled(enabled);
      setSnackbar({ open: true, message: "WhatsApp Voice settings saved.", severity: "success" });
    } catch (error) {
      // The save didn't take effect (Gupshup rejected it, or this
      // tenant/server isn't configured for voice yet) — revert the
      // toggle to the last-confirmed value rather than leave it showing
      // a change that never actually happened.
      setVoiceEnabled(savedVoiceEnabled);
      const message = error?.response?.data?.error || "Could not save WhatsApp Voice settings.";
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 640 }}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>
        WhatsApp Voice
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Turn on voice calling for this tenant.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "#ffffff",
              border: "1px solid #eceff1",
              borderRadius: "12px",
              px: 2.5,
              py: 2,
              mb: 3,
            }}
          >
            <Box sx={{ pr: 2 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                Enable voice calling
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", color: "#888", mt: 0.25 }}>
                Customers can call your WhatsApp Business number directly.
              </Typography>
            </Box>
            <Switch
              checked={voiceEnabled}
              onChange={(e) => setVoiceEnabled(e.target.checked)}
              disabled={saving}
            />
          </Box>

          <ButtonOne
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : null}
          >
            {saving ? "Saving..." : "Save changes"}
          </ButtonOne>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppVoiceSettings;
